import * as React from 'react';

import { cx } from '../../../utils/cx';
import CloseButton from '../buttons/CloseButton';
import Tooltip from '../Tooltip';

type CardSeverity = 'error' | 'warning';

interface ICardContext {
  severity: CardSeverity;
  clickable: boolean;
}

const CardContext = React.createContext<ICardContext | null>(null);

const useCardContext = () => {
  const context = React.useContext(CardContext);
  if (!context) {
    throw new Error('Card compound components must be used within a Card');
  }
  return context;
};

interface ICardProps extends React.PropsWithChildren {
  severity?: CardSeverity;
  onClick?: () => void;
  className?: string;
}

const CardComponent = ({ severity = 'warning', onClick, className, children }: ICardProps) => {
  const isError = severity === 'error';

  return (
    <CardContext.Provider value={{ severity, clickable: !!onClick }}>
      <div
        className={cx(
          'group flex min-w-[232px] flex-col gap-0 overflow-hidden rounded-xl border transition-colors duration-150',
          isError ? 'border-utility-error-200 bg-utility-error-25' : 'border-utility-warning-200 bg-utility-warning-25',
          onClick &&
            (isError ? 'hover:bg-utility-error-50 cursor-pointer' : 'hover:bg-utility-warning-50 cursor-pointer'),
          className
        )}
        onClick={onClick}
      >
        {children}
      </div>
    </CardContext.Provider>
  );
};

interface IHeaderProps extends React.PropsWithChildren {
  icon?: React.ReactNode;
  label?: string;
  onDismiss?: () => void;
  dismissTooltip?: React.ReactNode;
  className?: string;
}

const Header = ({ icon, label, onDismiss, dismissTooltip, className, children }: IHeaderProps) => {
  const { severity, clickable } = useCardContext();
  const isError = severity === 'error';

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className={cx(
        'flex h-11 items-center justify-between py-2 pl-4 pr-3 transition-colors duration-150',
        isError ? 'bg-utility-error-50' : 'bg-utility-warning-50',
        clickable && (isError ? 'group-hover:bg-utility-error-100' : 'group-hover:bg-utility-warning-100'),
        className
      )}
    >
      <div className="flex items-center gap-2">
        {icon && (
          <span
            className={cx(
              'flex size-5 shrink-0 items-center justify-center [&>svg]:size-5',
              isError ? 'text-utility-error-600' : 'text-utility-warning-600'
            )}
          >
            {icon}
          </span>
        )}
        {label && <span className="text-primary text-md font-semibold">{label}</span>}
        {children}
      </div>
      {onDismiss && (
        <div
          className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
          onClick={handleCloseClick}
        >
          <Tooltip title={dismissTooltip ?? ''} isDisabled={!dismissTooltip}>
            <CloseButton
              className="hover:bg-transparent"
              size="xs"
              theme="light"
              onPress={onDismiss}
              aria-label="Dismiss"
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

interface IBodyProps extends React.PropsWithChildren {
  className?: string;
}

const Body = ({ className, children }: IBodyProps) => {
  return <div className={cx('flex flex-1 flex-col gap-2 overflow-hidden px-4 pb-5 pt-2', className)}>{children}</div>;
};

interface ITextProps extends React.PropsWithChildren {
  className?: string;
}

const Text = ({ className, children }: ITextProps) => {
  return <p className={cx('text-secondary text-sm font-medium leading-5', className)}>{children}</p>;
};

export default Object.assign(CardComponent, {
  Header,
  Body,
  Text,
});
